import * as React from 'react'

import type { LucideIcon, LucideProps } from 'lucide-react'

import { PermitFieldType, WorkflowsFilterType } from '~/client/graph'

import { OPTION_GLYPHS } from '../components/OptionsList/optionGlyphs'
import { PERMIT_FIELD_GLYPHS } from '../components/PermitFieldIcon/permitFieldGlyphs'
import {
  FILE_TYPE_CATEGORY,
  PROJECT_CATEGORY,
} from '../components/WorkflowDirectory/components/WorkflowFilters/WorkflowFilters.types'
import { GenericWorkflowIcon } from '../components/WorkflowTypeIcon/WorkflowIconGroups'
import { FilterType } from '../enums/FilterType'
import { TagType } from '../enums/TagType'

/**
 * Location TagTypes that should be grouped under a single "Location" filter category.
 * Used to consolidate all location-related tags into one filter.
 */
export const LOCATION_TAG_TYPES: TagType[] = [
  TagType.Building,
  TagType.Zone,
  TagType.Level,
  TagType.Area,
  TagType.Gate,
  TagType.Route,
  TagType.ProjectLocation,
  TagType.Staging,
  TagType.LogisticsObject,
  TagType.VerticalObject,
  TagType.InteriorDoor,
  TagType.InteriorPath,
  TagType.Integration,
]

/**
 * Maps FilterType to PermitFieldType for includeUnassignedFields filter.
 * Used when filtering workflows that have unassigned values for specific fields.
 */
export const FILTER_TYPE_TO_PERMIT_FIELD_TYPE: Partial<
  Record<FilterType, PermitFieldType>
> = {
  [FilterType.Company]: PermitFieldType.Company,
  [FilterType.ResponsibleContact]: PermitFieldType.Assignee,
  [FilterType.Location]: PermitFieldType.Location,
  [FilterType.Equipment]: PermitFieldType.Equipment,
  [FilterType.Attachment]: PermitFieldType.File,
}

/**
 * Maps TagType (and synthetic categories) to WorkflowsFilterType for server-side filtering.
 * Includes all location tag types mapped to Location filter.
 */
export const TAG_TYPE_TO_WORKFLOWS_FILTER_TYPE: Record<
  string,
  WorkflowsFilterType
> = {
  [TagType.FormCategory]: WorkflowsFilterType.Category,
  [TagType.PermitType]: WorkflowsFilterType.Type,
  [TagType.Status]: WorkflowsFilterType.Status,
  [TagType.Company]: WorkflowsFilterType.Company,
  [TagType.User]: WorkflowsFilterType.ResponsibleContact,
  [TagType.OffloadingEquipment]: WorkflowsFilterType.Equipment,
  // All location types map to Location filter
  [TagType.Area]: WorkflowsFilterType.Location,
  [TagType.Building]: WorkflowsFilterType.Location,
  [TagType.Zone]: WorkflowsFilterType.Location,
  [TagType.Level]: WorkflowsFilterType.Location,
  [TagType.Gate]: WorkflowsFilterType.Location,
  [TagType.Route]: WorkflowsFilterType.Location,
  [TagType.Staging]: WorkflowsFilterType.Location,
  [TagType.LogisticsObject]: WorkflowsFilterType.Location,
  [TagType.VerticalObject]: WorkflowsFilterType.Location,
  [TagType.InteriorDoor]: WorkflowsFilterType.Location,
  [TagType.InteriorPath]: WorkflowsFilterType.Location,
  [TagType.Integration]: WorkflowsFilterType.Location,
  [TagType.ProjectLocation]: WorkflowsFilterType.Location,
  // Synthetic categories
  [FILE_TYPE_CATEGORY]: WorkflowsFilterType.Attachment,
  [PROJECT_CATEGORY]: WorkflowsFilterType.Project,
}

/**
 * Maps FilterType to WorkflowsFilterType for Unassigned handling and server requests.
 */
export const FILTER_TYPE_TO_WORKFLOWS_FILTER_TYPE: Record<
  FilterType,
  WorkflowsFilterType
> = {
  [FilterType.Category]: WorkflowsFilterType.Category,
  [FilterType.Type]: WorkflowsFilterType.Type,
  [FilterType.Status]: WorkflowsFilterType.Status,
  [FilterType.Location]: WorkflowsFilterType.Location,
  [FilterType.Equipment]: WorkflowsFilterType.Equipment,
  [FilterType.Company]: WorkflowsFilterType.Company,
  [FilterType.ResponsibleContact]: WorkflowsFilterType.ResponsibleContact,
  [FilterType.Attachment]: WorkflowsFilterType.Attachment,
  [FilterType.Project]: WorkflowsFilterType.Project,
}

/**
 * Glyph per FilterType category. Field-backed categories draw the glyph of their
 * FILTER_TYPE_TO_PERMIT_FIELD_TYPE field; Type draws the Workflow mark; the rest share the
 * Group by / Sort by glyphs.
 */
export const FILTER_TYPE_GLYPHS: Record<FilterType, LucideIcon> = {
  [FilterType.Category]: OPTION_GLYPHS.workflowCategory,
  [FilterType.Type]: GenericWorkflowIcon,
  [FilterType.Status]: OPTION_GLYPHS.workflowStatus,
  [FilterType.Project]: OPTION_GLYPHS.project,
  [FilterType.Location]: PERMIT_FIELD_GLYPHS[PermitFieldType.Location],
  [FilterType.Equipment]: PERMIT_FIELD_GLYPHS[PermitFieldType.Equipment],
  [FilterType.Company]: PERMIT_FIELD_GLYPHS[PermitFieldType.Company],
  [FilterType.ResponsibleContact]:
    PERMIT_FIELD_GLYPHS[PermitFieldType.Assignee],
  [FilterType.Attachment]: PERMIT_FIELD_GLYPHS[PermitFieldType.File],
}

export const buildFilterTypeIconMap = (
  props: LucideProps,
): Record<FilterType, JSX.Element> =>
  Object.fromEntries(
    Object.entries(FILTER_TYPE_GLYPHS).map(([filterType, Glyph]) => [
      filterType,
      <Glyph key={filterType} {...props} />,
    ]),
  ) as Record<FilterType, JSX.Element>

/**
 * Icon map for FilterType categories in DrillDownFilter: 16px category headers.
 */
export const FILTER_TYPE_ICON_MAP = buildFilterTypeIconMap({
  className: 'tw:size-4',
})
